import { nanoid } from "nanoid"
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"

import { useMutation, userClient } from "@trueskin-web/apis"

const transformPhotosOrderToPositions = (photosOrder) =>
  Object.keys(
    Object.fromEntries(
      Object.entries(photosOrder).sort(([, a], [, b]) => a - b)
    )
  )

const PhotosContext = createContext()

function PhotosProvider({
  photosOrder = { one: 1, two: 2, three: 3 },
  required = true,
  ...props
}) {
  const [uploadError, setUploadError] = useState()

  const positions = transformPhotosOrderToPositions(photosOrder)

  const [photos, setPhotos] = useState(positions.map(() => ({ key: nanoid() })))

  const hasRequiredPhotos = required
    ? photos.some((photo) => !!photo.file)
    : true

  const {
    mutateAsync: uploadImages,
    isLoading: isUploadImagesLoading,
    error: uploadImagesError,
  } = useMutation(async ({ onSuccess }) => {
    if (!hasRequiredPhotos) {
      return
    }

    const filteredPhotos = photos.filter((p) => p.file).map((p) => p.file)

    await userClient.uploadDocuments({ files: filteredPhotos })

    if (onSuccess) {
      await onSuccess()
    }
  })

  const handleAdd = useCallback(({ index, photo }) => {
    setPhotos((prevPhotos) => {
      const newPhotos = [...prevPhotos]

      newPhotos[index] = { ...photo, attachId: newPhotos[index]?.attachId }

      return newPhotos
    })
  }, [])

  const handleAddMultiple = useCallback((photos) => {
    setPhotos((prevPhotos) => {
      const newPhotos = [...prevPhotos]

      photos.forEach((photo, index) => {
        newPhotos[index] = { ...photo, attachId: newPhotos[index]?.attachId }
      })

      return newPhotos
    })
  }, [])

  const handleRemove = useCallback((index) => {
    setPhotos((prevPhotos) => {
      const newPhotos = [...prevPhotos]

      newPhotos[index] = {
        key: nanoid(),
      }

      return newPhotos
    })
  }, [])

  const handleClear = useCallback(() => {
    setPhotos((prevPhotos) => {
      return prevPhotos.map(() => ({ key: nanoid() }))
    })
  }, [])

  useEffect(() => setUploadError(uploadImagesError), [uploadImagesError])

  const value = useMemo(
    () => ({
      photos,
      hasRequiredPhotos,
      uploadImages,
      isUploadImagesLoading,
      uploadError,

      handleAdd,
      handleAddMultiple,
      handleRemove,
      handleClear,
    }),
    [
      photos,
      hasRequiredPhotos,
      uploadImages,
      isUploadImagesLoading,
      uploadError,

      handleAdd,
      handleAddMultiple,
      handleRemove,
      handleClear,
    ]
  )

  return <PhotosContext.Provider value={value} {...props} />
}

function usePhotos() {
  const context = useContext(PhotosContext)
  if (context === undefined) {
    throw new Error(`usePhotos must be used within a PhotosProvider`)
  }
  return context
}

export { PhotosProvider, usePhotos }
